
import { useEffect, useRef, useState } from 'react';

export default function Transition() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const phrases = [
    { text: 'Details', highlight: 'decide everything.' },
    { text: 'Taste', highlight: 'is trained.' },
    { text: 'Restraint', highlight: 'is a skill.' }
  ];

  const metrics = [
    { value: 15, suffix: '+', label: 'Years of Excellence' },
    { value: 500, suffix: '+', label: 'Projects Delivered' },
    { value: 100, suffix: '%', label: 'Client Satisfaction' },
    { value: 24, suffix: '/7', label: 'Dedicated Support' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (rect.top < windowHeight && rect.bottom > 0) {
        setIsVisible(true);
        const progress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)));
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getCurrentPhrase = () => {
    if (scrollProgress < 0.3) return 0;
    if (scrollProgress < 0.6) return 1;
    return 2;
  };

  const currentPhrase = getCurrentPhrase();

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[120vh] md:min-h-[150vh] overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0A0A0A 0%, #1A1A1A 50%, #0A0A0A 100%)'
      }}
    >
      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }}
      />

      {/* Radial spotlight */}
      <div 
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] md:w-[800px] h-[400px] md:h-[800px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(201,168,108,0.08) 0%, transparent 70%)',
          transform: `translate(-50%, ${-200 + scrollProgress * 100}px)`
        }}
      />

      {/* Sticky content container */}
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden px-4">
        
        {/* Animated phrases */}
        <div className="relative z-10 text-center mb-8 md:mb-16">
          {phrases.map((phrase, index) => (
            <div
              key={index}
              className={`transition-all duration-1000 ease-out ${
                currentPhrase === index 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : currentPhrase > index 
                    ? 'opacity-0 -translate-y-20 scale-95' 
                    : 'opacity-0 translate-y-20 scale-95'
              }`}
              style={{
                position: currentPhrase === index ? 'relative' : 'absolute',
                visibility: Math.abs(currentPhrase - index) <= 1 ? 'visible' : 'hidden'
              }}
            >
              <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-light tracking-wide text-[#F5F3EF] mb-2 md:mb-4">
                <span className="text-[#C9A86C]">{phrase.text}</span>
              </h2>
              <p className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extralight tracking-wider text-[#F5F3EF]/80 italic">
                {phrase.highlight}
              </p>
              
              {/* Animated underline */}
              <div 
                className={`h-[1px] bg-gradient-to-r from-transparent via-[#C9A86C] to-transparent mx-auto mt-4 md:mt-8 transition-all duration-1000 delay-300 ${
                  currentPhrase === index ? 'w-24 md:w-48 opacity-100' : 'w-0 opacity-0'
                }`}
              />
            </div>
          ))}
        </div>

        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Corner accents - Hidden on mobile */}
          <div 
            className={`absolute top-10 md:top-20 left-4 md:left-20 w-16 md:w-32 h-16 md:h-32 border-l border-t border-[#C9A86C]/20 transition-all duration-1000 hidden sm:block ${
              isVisible ? 'opacity-100 translate-x-0 translate-y-0' : 'opacity-0 -translate-x-10 -translate-y-10'
            }`}
          />
          <div 
            className={`absolute bottom-10 md:bottom-20 right-4 md:right-20 w-16 md:w-32 h-16 md:h-32 border-r border-b border-[#C9A86C]/20 transition-all duration-1000 delay-200 hidden sm:block ${
              isVisible ? 'opacity-100 translate-x-0 translate-y-0' : 'opacity-0 translate-x-10 translate-y-10'
            }`}
          />
          
          {/* Floating particles - Hidden on mobile */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-[#C9A86C]/40 hidden md:block"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                transform: `translateY(${Math.sin(scrollProgress * Math.PI * 2 + i) * 30}px)`,
                opacity: 0.3 + Math.sin(scrollProgress * Math.PI + i) * 0.3,
                transition: 'transform 0.5s ease-out'
              }}
            />
          ))}
        </div>

        {/* Craftsmanship images - Hidden on small screens */}
        <div className="absolute inset-0 hidden md:flex items-center justify-between px-4 md:px-8 lg:px-16 pointer-events-none">
          {/* Left image */}
          <div 
            className={`relative w-32 md:w-48 lg:w-64 h-40 md:h-64 lg:h-80 rounded-lg overflow-hidden transition-all duration-1000 ${
              scrollProgress > 0.1 ? 'opacity-60 translate-x-0' : 'opacity-0 -translate-x-20'
            }`}
            style={{
              transform: `translateY(${scrollProgress * -50}px)`
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20shot%20of%20skilled%20hands%20applying%20gold%20leaf%20to%20ornate%20wooden%20furniture%20detail%20showing%20precision%20craftsmanship%20warm%20lighting%20shallow%20depth%20of%20field%20luxury%20artisan%20workshop%20dark%20moody%20background&width=400&height=500&seq=trans1&orientation=portrait"
              alt="Craftsmanship detail"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
            <div className="absolute inset-0 border border-[#C9A86C]/30 rounded-lg" />
          </div>

          {/* Right image */}
          <div 
            className={`relative w-32 md:w-48 lg:w-64 h-40 md:h-64 lg:h-80 rounded-lg overflow-hidden transition-all duration-1000 delay-200 ${
              scrollProgress > 0.15 ? 'opacity-60 translate-x-0' : 'opacity-0 translate-x-20'
            }`}
            style={{
              transform: `translateY(${scrollProgress * -30}px)`
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=professional%20painter%20carefully%20applying%20premium%20paint%20with%20fine%20brush%20on%20luxury%20wall%20molding%20detail%20shot%20showing%20steady%20hands%20and%20precision%20warm%20studio%20lighting%20dark%20elegant%20background%20craftsmanship%20excellence&width=400&height=500&seq=trans2&orientation=portrait"
              alt="Precision painting"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
            <div className="absolute inset-0 border border-[#C9A86C]/30 rounded-lg" />
          </div>
        </div>

        {/* Bottom quote */}
        <div 
          className={`absolute bottom-24 md:bottom-32 left-1/2 -translate-x-1/2 text-center transition-all duration-1000 px-4 w-full ${
            scrollProgress > 0.5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-[#C9A86C]/60 text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] mb-2 md:mb-4">Our Philosophy</p>
          <p className="text-[#F5F3EF]/70 text-base md:text-lg lg:text-xl font-light italic max-w-xl mx-auto">
            "Excellence is not a destination, it's a continuous journey of refinement."
          </p>
        </div>

        {/* Scroll indicator - Hidden on mobile */}
        <div 
          className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center transition-opacity duration-500 hidden md:flex ${
            scrollProgress < 0.7 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-[1px] h-12 bg-gradient-to-b from-[#C9A86C]/50 to-transparent relative overflow-hidden">
            <div 
              className="absolute top-0 left-0 w-full h-4 bg-[#C9A86C]"
              style={{
                animation: 'scrollPulse 2s ease-in-out infinite'
              }}
            />
          </div>
        </div>
      </div>

      {/* Metrics section at bottom */}
      <div 
        className={`absolute bottom-0 left-0 right-0 py-8 md:py-16 bg-gradient-to-t from-black/80 to-transparent transition-all duration-1000 ${
          scrollProgress > 0.6 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {metrics.map((metric, index) => (
              <div 
                key={index}
                className="text-center relative"
                style={{
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <div className="relative inline-block">
                  <span 
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-[#C9A86C]"
                    style={{
                      textShadow: scrollProgress > 0.7 ? '0 0 30px rgba(201,168,108,0.3)' : 'none',
                      transition: 'text-shadow 0.5s ease'
                    }}
                  >
                    {scrollProgress > 0.65 ? metric.value : 0}{metric.suffix}
                  </span>
                </div>
                <p className="text-[#F5F3EF]/50 text-[10px] sm:text-xs uppercase tracking-[0.1em] md:tracking-[0.2em] mt-2 md:mt-4">
                  {metric.label}
                </p>
                {index < metrics.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-[1px] h-12 md:h-16 bg-[#F5F3EF]/10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress line at top */}
      <div className="fixed top-0 left-0 right-0 h-[2px] bg-[#1A1A1A] z-50">
        <div 
          className="h-full bg-gradient-to-r from-[#C9A86C] to-[#D4AF37] transition-all duration-300"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      <style>{`
        @keyframes scrollPulse {
          0%, 100% { transform: translateY(-100%); }
          50% { transform: translateY(200%); }
        }
      `}</style>
    </section>
  );
}
